import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../config';
import { toast, confirmDialog } from '../ui/notify';
import { getStationName } from '../utils/stationMapping';
import PersonalPlanningEnhanced from '../PersonalPlanningEnhanced';

// Schichtplanung fürs Handy (Philipp, 04.10.2026): Die große Ansicht lebt vom Ziehen mit der Maus – auf dem Handy
// ließ sich nichts ändern. Hier: je Station eine Liste der Einsätze; antippen → Zeiten, Station, Kasse ändern oder
// den Einsatz entfernen; „+ Person" legt einen neuen an. Nutzt dieselben Server-Routen wie die große Ansicht
// (gleiche Rechte, gleicher Doppelbelegungs-Check). Am PC bleibt alles, wie es war.
const STATIONS = ['G1', 'G5', 'G8', 'G10', 'WG'];
const pad = (n) => String(n).padStart(2, '0');
// Nacht-Reihenfolge: 00:00–04:59 zählt als „nach Mitternacht"
const nk = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return (h < 5 ? h + 24 : h) * 60 + (m || 0); };
// Auswahl in 15-Minuten-Schritten von 05:00 bis 01:00 (24:00 = Mitternacht)
const TIMES = (() => {
  const out = [];
  for (let m = 5 * 60; m <= 25 * 60; m += 15) {
    const h = Math.floor(m / 60), mi = m % 60;
    out.push(h < 24 ? `${pad(h)}:${pad(mi)}` : h === 24 && mi === 0 ? '24:00' : `${pad(h - 24)}:${pad(mi)}`);
  }
  return out;
})();
const toOption = (t) => (t === '00:00' ? '24:00' : t);
const timeLabel = (t) => (t === '24:00' ? '00:00 (Mitternacht)' : t);
const isoAdd = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const dayLabel = (iso) => `${WD[new Date(iso + 'T12:00:00').getDay()]} ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

function TimeSelect({ value, onChange }) {
  const opts = TIMES.includes(value) ? TIMES : [...TIMES, value].sort((a, b) => nk(a) - nk(b));
  return (
    <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {opts.map((t) => <option key={t} value={t}>{timeLabel(t)}</option>)}
    </select>
  );
}

function Sheet({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-100 p-3" style={{ maxWidth: 560, borderRadius: '16px 16px 0 0', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">{title}</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function MobileShiftPlanning({ user, onDesktop }) {
  const stations = user?.role === 'planner' ? [String(user.planner_station || 'WG').toUpperCase()] : STATIONS;
  const [date, setDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [need, setNeed] = useState({});
  const [users, setUsers] = useState([]);
  const [avail, setAvail] = useState({});
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // { a, start, end, station, cashier }
  const [add, setAdd] = useState(null);     // { station, q, userId, start, end }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [asg, plan, av] = await Promise.all([
        apiCall(`/planning/assignments/${date}`).catch(() => []),
        apiCall(`/flexible-shifts?date=${date}`).catch(() => []),
        apiCall(`/user-availability/${date}`).catch(() => []),
      ]);
      setRows(Array.isArray(asg) ? asg.filter((a) => a.status !== 'cancelled') : []);
      const nd = {};
      try {
        const p = Array.isArray(plan) && plan[0] ? JSON.parse(plan[0].plan_data) : null;
        const arr = p ? (Array.isArray(p.stations) ? p.stations : Object.values(p.stations || {})) : [];
        arr.forEach((s) => { const sid = String(s.station_id || s.id || '').toUpperCase(); nd[sid] = {}; Object.entries(s.hourlyStaffing || {}).forEach(([h, v]) => { nd[sid][Number(h)] = Number(v.needed) || 0; }); });
      } catch (_) { /* kein Bedarf hinterlegt */ }
      setNeed(nd);
      const am = {}; (Array.isArray(av) ? av : []).forEach((x) => { am[x.user_id] = x; });
      setAvail(am);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiCall('/users').then((u) => setUsers(Array.isArray(u) ? u : [])).catch(() => setUsers([])); }, []);

  const byStation = useMemo(() => {
    const m = {}; stations.forEach((s) => { m[s] = []; });
    rows.forEach((a) => { const s = String(a.station_id || a.station || '').toUpperCase(); if (m[s]) m[s].push(a); });
    Object.values(m).forEach((l) => l.sort((a, b) => nk(a.start_time) - nk(b.start_time)));
    return m;
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abweichungen vom Bedarf je Station, zu Zeitspannen zusammengefasst (Person zählt ab 30 Min in der Stunde)
  const gaps = (s) => {
    const nd = need[s]; if (!nd) return [];
    const out = [];
    for (let h = 5; h <= 24; h++) {
      const hv = (byStation[s] || []).filter((a) => { const s0 = nk(a.start_time); let e0 = nk(a.end_time === '00:00' ? '24:00' : a.end_time); if (e0 <= s0) e0 += 1440; return Math.min(e0, h * 60 + 60) - Math.max(s0, h * 60) >= 30; }).length;
      const n = nd[h] || 0; if (hv === n) continue;
      const last = out[out.length - 1];
      if (last && last.to === h && last.hv === hv && last.n === n) last.to = h + 1; else out.push({ from: h, to: h + 1, hv, n });
    }
    return out;
  };

  const openEdit = (a) => setEdit({ a, start: toOption(a.start_time), end: toOption(a.end_time), station: String(a.station_id || '').toUpperCase(), cashier: !!a.is_cashier });

  const saveEdit = async () => {
    const { a, start, end, station, cashier } = edit;
    const s0 = nk(start), e0 = nk(end); // 24:00 und 00:00 zählen beide als Mitternacht am Tagesende
    if (e0 - s0 < 30) { toast('Das Ende muss mindestens 30 Minuten nach dem Beginn liegen.', 'warning'); return; }
    setBusy(true);
    try {
      // unveränderte Zeiten in der gespeicherten Schreibweise zurückgeben (00:00 / 24:00 kommen beide vor)
      const st = start === toOption(a.start_time) ? a.start_time : start;
      const en = end === toOption(a.end_time) ? a.end_time : end;
      const changed = st !== a.start_time || en !== a.end_time || station !== String(a.station_id || '').toUpperCase();
      if (changed) {
        await apiCall(`/planning/assign/${a.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: a.date, user_id: a.user_id, station_id: station, start_time: st, end_time: en, is_cashier: !!a.is_cashier }) });
      }
      if (cashier !== !!a.is_cashier) {
        await apiCall(`/daily-shifts/${a.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_cashier: cashier }) });
      }
      toast(changed || cashier !== !!a.is_cashier ? 'Gespeichert' : 'Nichts geändert', 'success');
      setEdit(null); load();
    } catch (e) { toast(e.message || 'Speichern fehlgeschlagen', 'danger'); load(); }
    finally { setBusy(false); }
  };

  const removeEdit = async () => {
    const { a } = edit;
    if (!(await confirmDialog(`${a.user_name || 'Diesen Einsatz'} am ${dayLabel(a.date)} (${a.start_time}–${a.end_time}, ${getStationName(a.station_id)}) wirklich entfernen?`))) return;
    setBusy(true);
    try { await apiCall(`/planning/assign/${a.id}`, { method: 'DELETE' }); toast('Einsatz entfernt', 'success'); setEdit(null); load(); }
    catch (e) { toast(e.message || 'Entfernen fehlgeschlagen', 'danger'); load(); }
    finally { setBusy(false); }
  };

  const openAdd = (station) => {
    const nd = need[station] || {};
    const hrs = Object.keys(nd).map(Number).filter((h) => nd[h] > 0).sort((a, b) => a - b);
    setAdd({ station, q: '', userId: null, start: hrs.length ? `${pad(hrs[0])}:00` : '10:00', end: '18:00' });
  };
  const saveAdd = async () => {
    const s0 = nk(add.start), e0 = nk(add.end);
    if (!add.userId) { toast('Bitte eine Person auswählen.', 'warning'); return; }
    if (e0 - s0 < 30) { toast('Das Ende muss mindestens 30 Minuten nach dem Beginn liegen.', 'warning'); return; }
    setBusy(true);
    try {
      await apiCall('/planning/assign', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, user_id: add.userId, station_id: add.station, start_time: add.start, end_time: add.end, is_cashier: false }) });
      toast('Eingeplant', 'success'); setAdd(null); load();
    } catch (e) { toast(e.message || 'Einplanen fehlgeschlagen', 'danger'); load(); }
    finally { setBusy(false); }
  };

  const candidates = useMemo(() => {
    if (!add) return [];
    const q = add.q.trim().toLowerCase();
    const planned = {}; rows.forEach((a) => { (planned[a.user_id] = planned[a.user_id] || []).push(a); });
    return users
      .filter((u) => ['employee', 'mitarbeiter', 'lead', 'planner'].includes(u.role) || u.role_id === 2)
      .filter((u) => !q || `${u.firstname} ${u.lastname}`.toLowerCase().includes(q))
      .map((u) => ({ u, av: avail[u.id], planned: planned[u.id] || [] }))
      .sort((a, b) => ((b.av && b.av.is_available === 1) ? 1 : 0) - ((a.av && a.av.is_available === 1) ? 1 : 0) || String(a.u.lastname).localeCompare(String(b.u.lastname)))
      .slice(0, q ? 30 : 15);
  }, [add, users, avail, rows]);

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="mb-0">Schichtplanung</h4>
        {onDesktop && <button className="btn btn-link btn-sm p-0" onClick={onDesktop}>Große Ansicht</button>}
      </div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-outline-secondary" onClick={() => setDate(isoAdd(date, -1))}>◀</button>
        <input type="date" className="form-control text-center fw-bold" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <button className="btn btn-outline-secondary" onClick={() => setDate(isoAdd(date, 1))}>▶</button>
        {date !== todayIso() && <button className="btn btn-outline-primary" onClick={() => setDate(todayIso())}>Heute</button>}
      </div>
      <div className="text-muted small mb-2">{dayLabel(date)} · {rows.length} Einsätze{loading ? ' · lädt …' : ''}</div>

      {stations.map((s) => {
        const g = gaps(s);
        const under = g.filter((x) => x.hv < x.n), over = g.filter((x) => x.hv > x.n);
        const hh = (h) => pad(h % 24);
        return (
          <div key={s} className="card mb-3 shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center py-2">
              <strong>{getStationName(s)} <span className="text-muted fw-normal">({(byStation[s] || []).length})</span></strong>
              <button className="btn btn-sm btn-success" onClick={() => openAdd(s)}>+ Person</button>
            </div>
            {(under.length > 0 || over.length > 0) && (
              <div className="px-3 py-1 small border-bottom" style={{ background: under.length ? '#fff5f5' : '#fffdf0' }}>
                {under.length > 0 && <div className="text-danger">zu wenig: {under.map((x) => `${hh(x.from)}–${hh(x.to)} (${x.hv}/${x.n})`).join(', ')}</div>}
                {over.length > 0 && <div className="text-muted">mehr als nötig: {over.map((x) => `${hh(x.from)}–${hh(x.to)} (${x.hv}/${x.n})`).join(', ')}</div>}
              </div>
            )}
            <div className="list-group list-group-flush">
              {(byStation[s] || []).length === 0 && <div className="list-group-item text-muted small">Niemand eingeplant.</div>}
              {(byStation[s] || []).map((a) => (
                <button key={a.id} type="button" className="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2" onClick={() => openEdit(a)}>
                  <span className="fw-bold text-nowrap" style={{ minWidth: 104 }}>{a.start_time}–{a.end_time}</span>
                  <span className="flex-grow-1 text-start">{a.user_name || `#${a.user_id}`}
                    {a.absence && <span className="badge bg-danger ms-1">{a.absence === 'sick' ? 'krank' : 'nicht erschienen'}</span>}
                  </span>
                  {!!a.is_cashier && <span className="text-nowrap small">💶{a.cashier_start && a.cashier_end ? ` ${a.cashier_start}–${a.cashier_end}` : ''}</span>}
                  <span className="text-muted">›</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {edit && (
        <Sheet title={edit.a.user_name || 'Einsatz'} onClose={() => !busy && setEdit(null)}>
          <div className="text-muted small mb-2">{dayLabel(edit.a.date)} · bisher {edit.a.start_time}–{edit.a.end_time}, {getStationName(edit.a.station_id)}</div>
          <div className="row g-2 mb-2">
            <div className="col-6"><label className="form-label small mb-0">Beginn</label><TimeSelect value={edit.start} onChange={(v) => setEdit({ ...edit, start: v })} /></div>
            <div className="col-6"><label className="form-label small mb-0">Ende</label><TimeSelect value={edit.end} onChange={(v) => setEdit({ ...edit, end: v })} /></div>
          </div>
          {stations.length > 1 && (
            <div className="mb-2"><label className="form-label small mb-0">Station</label>
              <select className="form-select" value={edit.station} onChange={(e) => setEdit({ ...edit, station: e.target.value })}>
                {stations.map((s) => <option key={s} value={s}>{getStationName(s)}</option>)}
              </select>
            </div>
          )}
          <div className="form-check form-switch mb-3">
            <input className="form-check-input" type="checkbox" id="msp-kasse" checked={edit.cashier} onChange={(e) => setEdit({ ...edit, cashier: e.target.checked })} />
            <label className="form-check-label" htmlFor="msp-kasse">💶 Kasse{edit.a.cashier_start && edit.a.cashier_end && edit.cashier ? ` (Zeitfenster ${edit.a.cashier_start}–${edit.a.cashier_end})` : ''}</label>
          </div>
          <div className="d-grid gap-2">
            <button className="btn btn-primary btn-lg" disabled={busy} onClick={saveEdit}>Speichern</button>
            <button className="btn btn-outline-danger" disabled={busy} onClick={removeEdit}>Einsatz entfernen</button>
          </div>
          <div className="text-muted small mt-2">Die Person bekommt keine automatische Nachricht – bitte selbst Bescheid geben.</div>
        </Sheet>
      )}

      {add && (
        <Sheet title={`Person einplanen – ${getStationName(add.station)}`} onClose={() => !busy && setAdd(null)}>
          <div className="row g-2 mb-2">
            <div className="col-6"><label className="form-label small mb-0">Beginn</label><TimeSelect value={add.start} onChange={(v) => setAdd({ ...add, start: v })} /></div>
            <div className="col-6"><label className="form-label small mb-0">Ende</label><TimeSelect value={add.end} onChange={(v) => setAdd({ ...add, end: v })} /></div>
          </div>
          <input className="form-control mb-2" placeholder="Name suchen …" value={add.q} onChange={(e) => setAdd({ ...add, q: e.target.value, userId: null })} />
          <div className="list-group mb-3" style={{ maxHeight: '38vh', overflowY: 'auto' }}>
            {candidates.map(({ u, av, planned }) => (
              <button key={u.id} type="button" className={`list-group-item list-group-item-action py-2 ${add.userId === u.id ? 'active' : ''}`} onClick={() => setAdd({ ...add, userId: u.id })}>
                <div className="fw-bold">{u.firstname} {u.lastname}</div>
                <div className="small" style={{ opacity: 0.85 }}>
                  {av ? (av.is_available === 1 ? `verfügbar${av.start_time && av.end_time && !(av.start_time === '00:00' && av.end_time === '23:59') ? ` ${av.start_time}–${av.end_time}` : ' ganztägig'}` : 'nicht verfügbar') : 'keine Verfügbarkeit eingetragen'}
                  {planned.length > 0 && ` · schon eingeplant: ${planned.map((p) => `${p.start_time}–${p.end_time} ${String(p.station_id || '').toUpperCase()}`).join(', ')}`}
                </div>
              </button>
            ))}
            {candidates.length === 0 && <div className="list-group-item text-muted small">Niemand gefunden.</div>}
          </div>
          <div className="d-grid"><button className="btn btn-success btn-lg" disabled={busy || !add.userId} onClick={saveAdd}>Einplanen</button></div>
        </Sheet>
      )}
    </div>
  );
}

// Schichtplanung: am Handy die Listen-Ansicht, sonst die große Ansicht – jeweils umschaltbar (Wahl wird gemerkt).
export default function PlanningResponsive({ user }) {
  const narrow = () => (typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobile, setMobile] = useState(() => {
    let pref = null; try { pref = window.localStorage.getItem('planningView'); } catch (_) { pref = null; }
    return pref === 'mobile' ? true : pref === 'desktop' ? false : narrow();
  });
  const choose = (m) => { setMobile(m); try { window.localStorage.setItem('planningView', m ? 'mobile' : 'desktop'); } catch (_) { /* egal */ } };
  if (mobile) return <MobileShiftPlanning user={user} onDesktop={() => choose(false)} />;
  return (
    <>
      {narrow() && <div className="mb-2"><button className="btn btn-outline-primary btn-sm" onClick={() => choose(true)}>📱 Handy-Ansicht (zum Ändern)</button></div>}
      <PersonalPlanningEnhanced user={user} />
    </>
  );
}
